import type { QuoteSettings } from '@/types/organization'
import type { GraphQLResult } from 'aws-amplify/api'
import { format } from 'date-fns'
import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { organizationAdapter, teamAdapter } from '@skop-app/lib-domain/adapters'
import { catalogSettings } from '@skop-app/lib-domain/constants'
import type {
  OrganizationDefaults,
  OrganizationMember,
  OrganizationMemberSchema,
  OrganizationSettingsProps,
  Subscription,
  Team,
  UserTeam,
} from '@skop-app/lib-domain/types'

import { memberAdapter } from '@/services/adapters/member'
import { getAmplifyClient } from '@/services/amplifyClient'
import {
  fetchSupportPayload,
  useSupportTarget,
} from '@/services/support/support.client'
import {
  supportQueryKey,
  type SupportTarget,
} from '@/services/support/supportContract'
import {
  getOrganizationMembersbyProjectIDQuery,
  getOrganizationMembersQuery,
  getOrganizationMetadataQuery,
  getOrganizationSummaryQuery,
  getOrganizationTreeQuery,
} from '@/services/graphql'
import { referenceQueryPreset } from '@/services/query-presets'
import type { Language } from '@/types/translations'
import type { SeatAwareOrganizationMember } from '@/types/seatAccess'

const client = getAmplifyClient()

type MetadataEntry = {
  key: string
  type: string
  value: unknown
}

type OrganizationShop = {
  id: string
  enabled: string
  enablePassword: boolean
}

type OrganizationRelationSummary = {
  id: string
  name: string
  color: string
  logo?: string | null
  partner?: string | null
  languages?: string[] | null
  shops?: {
    items?: OrganizationShop[] | null
  } | null
  properties?: {
    items?: { id: string }[] | null
  } | null
}

export type OrganizationSummarySchema = OrganizationDefaults & {
  quoteSettings?: QuoteSettings | null
  status?: string | null
  createdAt: string
  generateSummary: boolean
  shops: {
    items: OrganizationShop[]
  }
  subscriptions: {
    items: Subscription[]
  }
  properties: {
    items: { id: string }[]
  }
  parent?: OrganizationRelationSummary | null
  master?: OrganizationRelationSummary | null
}

type TeamWithOrganizationStatus = Team & {
  quoteSettings?: QuoteSettings | null
  languages?: OrganizationSummarySchema['languages']
  status?: string
}

type OrganizationSummaryQuery = {
  getOrganization?: OrganizationSummarySchema | null
}

type OrganizationMetadataQuery = {
  getOrganization?: {
    id: string
    metadata?: Array<{
      key: string
      type: string
      value: string
    }> | null
  } | null
}

type OrganizationMembersQuery = {
  getOrganization?: {
    id: string
    members?: {
      items?: OrganizationMemberSchema[] | null
      nextToken?: string | null
    } | null
  } | null
}

type AuthorizedOrganizationTreeMember = {
  id: string
  userID?: string | null
  organizationID: string
  role: string
  status?: string | null
  type?: string | null
  name?: string | null
  email?: string | null
}

type AuthorizedOrganizationTreeOrganization = {
  id: string
  name: string
  parentID?: string | null
  masterID?: string | null
  partner?: string | null
  color?: string | null
  logo?: string | null
  isMaster?: boolean | null
  members?: AuthorizedOrganizationTreeMember[] | null
}

type AuthorizedOrganizationTreeQuery = {
  getAuthorizedOrganizationTree?: {
    organizations?: AuthorizedOrganizationTreeOrganization[] | null
  } | null
}

const DEFAULT_SUBSCRIPTION: Subscription = {
  id: null,
  status: 'ACTIVE',
  type: 'FREE',
}

const normalizeMetadataKeys = (keys?: readonly string[]) =>
  keys?.length ? [...new Set(keys)].sort().join(',') : 'all'

const setNameIfPresent = (
  target: Record<string, string>,
  id: unknown,
  label: unknown,
) => {
  if (typeof id !== 'string' || id.length === 0) return
  if (typeof label !== 'string' || label.length === 0) return
  target[id] = label
}

export const organizationKeys = {
  summary: (id: string | null | undefined) =>
    ['organization-summary', id] as const,
  metadataScope: (id: string | null | undefined) =>
    ['organization-metadata', id] as const,
  metadata: (id: string | null | undefined, keys?: readonly string[]) =>
    ['organization-metadata', id, normalizeMetadataKeys(keys)] as const,
  members: (id: string | null | undefined) =>
    ['organization-members', id] as const,
  membersByProject: (projectID: string | null | undefined) =>
    ['organization-members-by-project-id', projectID ?? null] as const,
  tree: (id: string | null | undefined) => ['organization-tree', id] as const,
}

export const buildOrganizationNamesById = (params: {
  team?: Pick<Team, 'id' | 'name'>
  organizationParent?: Pick<Team, 'id' | 'name'>
  teams?: UserTeam[]
}): Record<string, string> => {
  const { team, organizationParent, teams = [] } = params
  const lookup: Record<string, string> = {}

  setNameIfPresent(lookup, team?.id, team?.name)
  setNameIfPresent(lookup, organizationParent?.id, organizationParent?.name)

  teams.forEach((entry) => {
    setNameIfPresent(lookup, entry.value, entry.label)
    setNameIfPresent(lookup, entry.id, entry.label)
  })

  return lookup
}

const safeJSONParse = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

const pickLatestSubscription = (
  items: Subscription[] | undefined,
): Subscription => {
  const subscriptions = items ?? []
  if (subscriptions.length === 0) return DEFAULT_SUBSCRIPTION

  const activeSubscriptions = subscriptions.filter(
    (subscription) =>
      subscription.status === 'ACTIVE' || subscription.status === 'TRIALING',
  )

  return (
    [...(activeSubscriptions.length ? activeSubscriptions : subscriptions)]
      .sort((a, b) => (a.start ?? '').localeCompare(b.start ?? ''))
      .reverse()[0] ?? DEFAULT_SUBSCRIPTION
  )
}

const SUPPORTED_LANGUAGES: readonly Language[] = [
  'FR',
  'EN',
  'DE',
  'IT',
  'NL',
  'ES',
]

const normalizeOrganizationLanguages = (
  languages: unknown,
): Language[] | undefined => {
  const normalized = (Array.isArray(languages) ? languages : []).flatMap(
    (item) => {
      const value =
        typeof item === 'string'
          ? item
          : item && typeof item === 'object' && 'value' in item
            ? (item as { value?: unknown }).value
            : undefined

      if (typeof value !== 'string') return []

      const upper = value.toUpperCase()
      return SUPPORTED_LANGUAGES.includes(upper as Language)
        ? [upper as Language]
        : []
    },
  )

  const unique = Array.from(new Set(normalized))
  return unique.length > 0 ? unique : undefined
}

export const resolveOrganizationLanguages = (
  ...sources: unknown[]
): Language[] => {
  for (const source of sources) {
    const normalized = normalizeOrganizationLanguages(source)
    if (normalized) return normalized
  }

  return ['FR']
}

const buildCustomSettings = (
  organization: OrganizationSummarySchema,
): OrganizationSettingsProps => ({
  id: organization.id ?? null,
  name: organization.name ?? catalogSettings.name,
  template: organization.template ?? catalogSettings.template,
  color: organization.color ?? catalogSettings.color,
  generateSummary:
    organization.generateSummary ?? catalogSettings.generateSummary,
  tagline: organization.tagline ?? catalogSettings.tagline,
  legal: organization.legal ?? catalogSettings.legal,
  logo: organization.logo ?? catalogSettings.logo,
  languages: resolveOrganizationLanguages(
    organization.languages,
    organization.parent?.languages,
    organization.master?.languages,
  ),
  propertyId:
    organization.properties.items?.[0]?.id ?? catalogSettings.propertyId,
})

const adaptOrganizationRelation = (
  relation?: OrganizationRelationSummary | null,
) => {
  if (!relation) return undefined

  const shop = relation.shops?.items?.[0]
  return {
    id: relation.id,
    name: relation.name,
    color: relation.color,
    logo: relation.logo ?? undefined,
    shopID: shop?.id,
    shop,
    partner: relation.partner ?? undefined,
    propertyId: relation.properties?.items?.[0]?.id,
  }
}

const adaptOrganizationSummary = (
  organization: OrganizationSummarySchema,
  support = false,
): TeamWithOrganizationStatus => {
  const propertyId = organization.properties.items?.[0]?.id
  const parent = adaptOrganizationRelation(organization.parent)
  const master = adaptOrganizationRelation(organization.master)
  const shop = organization.shops.items?.[0] ?? parent?.shop

  return {
    id: organization.id,
    name: organization.name,
    languages: organization.languages,
    tagline: organization.tagline ?? undefined,
    legal: organization.legal ?? undefined,
    color: organization.color,
    status: organization.status ?? undefined,
    template: organization.template,
    logo: organization.logo ?? undefined,
    SIRET: organization.SIRET ?? undefined,
    VAT: organization.VAT ?? undefined,
    email: organization.email ?? undefined,
    phone: organization.phone ?? undefined,
    address: organization.address ?? undefined,
    zipcode: organization.zipcode ?? undefined,
    city: organization.city ?? undefined,
    country: organization.country ?? undefined,
    quoteSettings: organization.quoteSettings,
    date: format(new Date(organization.createdAt), 'dd/MM/yyyy'),
    shopID: shop?.id,
    shop,
    customSettings: buildCustomSettings(organization),
    subscription: support
      ? [...organization.subscriptions.items].sort((a, b) => {
          const active = (entry: Subscription) =>
            Number(entry.status === 'ACTIVE' || entry.status === 'TRIALING')
          return (
            active(b) - active(a) ||
            (b.start ?? '').localeCompare(a.start ?? '')
          )
        })[0]
      : pickLatestSubscription(organization.subscriptions.items),
    master,
    parent,
    partner:
      organization.partner ??
      organization.parent?.partner ??
      organization.master?.partner ??
      undefined,
    isMaster: organization.isMaster ?? false,
    teams: [],
    members: [],
    propertyId,
    metadata: [],
    parentID: organization.parentID ?? undefined,
    masterID: organization.masterID ?? undefined,
  }
}

const adaptOrganizationMetadata = (
  metadata: OrganizationMetadataQuery['getOrganization'],
): MetadataEntry[] =>
  metadata?.metadata?.map((entry) => ({
    key: entry.key,
    type: entry.type,
    value: safeJSONParse(entry.value),
  })) ?? []

export type SupportOrganizationProjection = {
  summary: OrganizationSummarySchema & {
    parent?: OrganizationSummarySchema | null
    master?: OrganizationSummarySchema | null
  }
  metadata: NonNullable<
    OrganizationMetadataQuery['getOrganization']
  >['metadata']
  members: OrganizationMemberSchema[]
}

const supportOrganizationQuery = /* GraphQL */ `
  query ConsoleSupportOrganization($targetUserID: ID!, $organizationID: ID!) {
    consoleSupportOrganization(
      targetUserID: $targetUserID
      organizationID: $organizationID
    ) {
      payload
    }
  }
`

export async function fetchSupportOrganization(
  id: string,
  target: SupportTarget,
  signal?: AbortSignal,
) {
  const projection = await fetchSupportPayload<SupportOrganizationProjection>(
    supportOrganizationQuery,
    'consoleSupportOrganization',
    { targetUserID: target.targetUserID, organizationID: id },
    signal,
  )
  if (
    projection?.summary?.id !== id ||
    !Array.isArray(projection.metadata) ||
    !Array.isArray(projection.members)
  ) {
    throw new Error('Support organization projection could not be confirmed')
  }
  return projection
}

export const fetchOrganizationSummary = async ({
  id,
  supportTarget,
  signal,
  supportScopeOrganizationID,
}: {
  id: string
  supportTarget?: SupportTarget | null
  signal?: AbortSignal
  supportScopeOrganizationID?: string | null
}): Promise<TeamWithOrganizationStatus | null> => {
  if (supportTarget) {
    const { summary } = await fetchSupportOrganization(
      supportScopeOrganizationID ?? id,
      supportTarget,
      signal,
    )
    // Parent configuration is an explicit shallow projection of the validated
    // child scope. Never request the parent's members or claim parent membership.
    const projected =
      summary.id === id
        ? summary
        : [summary.parent, summary.master].find((row) => row?.id === id)
    if (!projected || !Array.isArray(projected.subscriptions?.items))
      throw new Error('Support organization summary could not be confirmed')
    return adaptOrganizationSummary(projected, true)
  }
  const { data } = (await client.graphql({
    query: getOrganizationSummaryQuery,
    variables: { id },
  })) as GraphQLResult<OrganizationSummaryQuery>

  return data?.getOrganization
    ? adaptOrganizationSummary(data.getOrganization)
    : null
}

export const fetchOrganizationMetadata = async ({
  id,
  supportTarget,
  signal,
}: {
  id: string
  supportTarget?: SupportTarget | null
  signal?: AbortSignal
}): Promise<MetadataEntry[]> => {
  if (supportTarget) {
    const projection = await fetchSupportOrganization(id, supportTarget, signal)
    return adaptOrganizationMetadata({ id, metadata: projection.metadata })
  }
  const { data } = (await client.graphql({
    query: getOrganizationMetadataQuery,
    variables: { id },
  })) as GraphQLResult<OrganizationMetadataQuery>

  return adaptOrganizationMetadata(data?.getOrganization)
}

const fetchOrganizationMembers = async ({
  id,
  supportTarget,
  signal,
}: {
  id: string
  supportTarget?: SupportTarget | null
  signal?: AbortSignal
}): Promise<SeatAwareOrganizationMember[]> => {
  if (supportTarget)
    return (await fetchSupportOrganization(id, supportTarget, signal)).members
      .filter(
        (member) =>
          member.status !== 'DECLINED' && member.status !== 'ARCHIVED',
      )
      .map(memberAdapter)
  const members: OrganizationMemberSchema[] = []
  const seenTokens = new Set<string>()
  let nextToken: string | undefined

  while (true) {
    const result = (await client.graphql({
      query: getOrganizationMembersQuery,
      variables: { id, nextToken },
    })) as GraphQLResult<OrganizationMembersQuery>

    if (result.errors?.length) {
      throw new Error(
        result.errors.map((error) => error.message).join('; ') ||
          'Failed to load organization members',
      )
    }

    const page = result.data?.getOrganization?.members
    if (!page) throw new Error('Failed to load organization members')
    members.push(...(page.items ?? []).filter(Boolean))

    const pageNextToken = page.nextToken ?? undefined
    if (!pageNextToken) break
    if (seenTokens.has(pageNextToken)) {
      throw new Error(
        'Detected repeated nextToken while listing organization members',
      )
    }
    seenTokens.add(pageNextToken)
    nextToken = pageNextToken
  }

  return members
    .filter(
      (member) => member.status !== 'DECLINED' && member.status !== 'ARCHIVED',
    )
    .map((member) => memberAdapter(member))
}

const TREE_EPOCH = new Date(0).toISOString()
const TREE_DEFAULT_COLOR = '#CCC'
type RawTeam = Parameters<typeof teamAdapter>[0]
type RawTeamMember = RawTeam['members']['items'][number]

const buildTreeRelation = (
  organization: AuthorizedOrganizationTreeOrganization,
) => ({
  id: organization.id,
  name: organization.name,
  color: organization.color ?? TREE_DEFAULT_COLOR,
  logo: organization.logo ?? undefined,
  partner: organization.partner ?? undefined,
  shops: { items: [] },
  properties: { items: [] },
})

const buildTreeMember = (
  member: AuthorizedOrganizationTreeMember,
  organization: AuthorizedOrganizationTreeOrganization,
): RawTeamMember =>
  ({
    id: member.id,
    userID: member.userID ?? '',
    organizationID: member.organizationID,
    role: member.role,
    status: member.status ?? '',
    type: member.type ?? 'ORGANIZATION',
    email: member.email ?? undefined,
    invitationDetails:
      member.status === 'PENDING' && member.name
        ? JSON.stringify({
            name: member.name,
            organizationName: organization.name,
          })
        : undefined,
    updatedAt: TREE_EPOCH,
    member: {
      name: member.name ?? undefined,
      email: member.email ?? undefined,
    },
    organization: {
      id: organization.id,
      name: organization.name,
      color: organization.color ?? TREE_DEFAULT_COLOR,
      isMaster: organization.isMaster ?? false,
      members: { items: [] },
    },
  }) as RawTeamMember

const buildRawTree = (
  organization: AuthorizedOrganizationTreeOrganization,
  byID: Map<string, AuthorizedOrganizationTreeOrganization>,
  childrenByParentID: Map<string, AuthorizedOrganizationTreeOrganization[]>,
  ancestors: ReadonlySet<string> = new Set(),
): RawTeam => {
  if (ancestors.has(organization.id)) {
    throw new Error('Organization tree contains a cycle')
  }

  const nextAncestors = new Set(ancestors)
  nextAncestors.add(organization.id)
  const parent = organization.parentID
    ? byID.get(organization.parentID)
    : undefined
  const master = organization.masterID
    ? byID.get(organization.masterID)
    : undefined
  const children = (childrenByParentID.get(organization.id) ?? []).filter(
    (child) => !nextAncestors.has(child.id),
  )

  return {
    id: organization.id,
    name: organization.name,
    color: organization.color ?? TREE_DEFAULT_COLOR,
    logo: organization.logo ?? undefined,
    partner: organization.partner ?? undefined,
    isMaster: organization.isMaster ?? !organization.parentID,
    parentID: organization.parentID ?? undefined,
    masterID: organization.masterID ?? undefined,
    createdAt: TREE_EPOCH,
    template: 'sheet',
    generateSummary: false,
    shops: { items: [] },
    subscriptions: { items: [DEFAULT_SUBSCRIPTION] },
    properties: { items: [] },
    metadata: [],
    parent: parent ? buildTreeRelation(parent) : undefined,
    master: master ? buildTreeRelation(master) : undefined,
    members: {
      items: (organization.members ?? []).map((member) =>
        buildTreeMember(member, organization),
      ),
    },
    teams: {
      items: children.map((child) => ({
        data: teamAdapter(
          buildRawTree(child, byID, childrenByParentID, nextAncestors),
        ),
      })),
    },
  } as unknown as RawTeam
}

export const adaptAuthorizedOrganizationTree = (
  response: AuthorizedOrganizationTreeQuery['getAuthorizedOrganizationTree'],
  requestedOrganizationID: string,
): Team | null => {
  const organizations = response?.organizations?.filter(Boolean) ?? []
  const selected = organizations.find(
    (organization) => organization.id === requestedOrganizationID,
  )
  if (!selected) return null

  const byID = new Map(
    organizations.map((organization) => [organization.id, organization]),
  )
  const childrenByParentID = new Map<
    string,
    AuthorizedOrganizationTreeOrganization[]
  >()

  organizations.forEach((organization) => {
    if (!organization.parentID) return
    const children = childrenByParentID.get(organization.parentID) ?? []
    children.push(organization)
    childrenByParentID.set(organization.parentID, children)
  })

  const rawTree = buildRawTree(selected, byID, childrenByParentID)
  return organizationAdapter(teamAdapter(rawTree)) as Team
}

const fetchOrganizationTree = async ({
  id,
}: {
  id: string
}): Promise<Team | null> => {
  return getOrganizationTree({ id })
}

const getOrganizationTree = async ({
  id,
}: {
  id: string
}): Promise<Team | null> => {
  try {
    const { data } = (await client.graphql({
      query: getOrganizationTreeQuery,
      variables: { organizationID: id },
    })) as GraphQLResult<AuthorizedOrganizationTreeQuery>

    return adaptAuthorizedOrganizationTree(
      data?.getAuthorizedOrganizationTree,
      id,
    )
  } catch (error) {
    console.error('getOrganizationTree', error)
    throw error
  }
}

export const useSelectedTeamSummary = ({
  id,
  supportScopeOrganizationID,
}: {
  id: string | null | undefined
  supportScopeOrganizationID?: string | null
}) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    queryKey: supportQueryKey(
      organizationKeys.summary(id),
      supportTarget,
      supportScopeOrganizationID,
    ),
    queryFn: ({ signal }) =>
      fetchOrganizationSummary({
        id: id as string,
        supportTarget,
        signal,
        supportScopeOrganizationID,
      }),
    ...referenceQueryPreset,
    enabled: Boolean(id),
  })
}

export const useOrganizationMetadata = (
  id: string | null | undefined,
  keys?: readonly string[],
) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    queryKey: supportQueryKey(
      organizationKeys.metadata(id, keys),
      supportTarget,
    ),
    queryFn: ({ signal }) =>
      fetchOrganizationMetadata({ id: id as string, supportTarget, signal }),
    ...referenceQueryPreset,
    enabled: Boolean(id),
    select: (metadata) => {
      if (!keys?.length) return metadata
      const keySet = new Set(keys)
      return metadata.filter((entry) => keySet.has(entry.key))
    },
  })
}

export const useOrganizationMembers = (id: string | null | undefined) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    queryKey: supportQueryKey(organizationKeys.members(id), supportTarget),
    queryFn: ({ signal }) =>
      fetchOrganizationMembers({ id: id as string, supportTarget, signal }),
    ...referenceQueryPreset,
    enabled: Boolean(id),
  })
}

export const useOrganizationTree = (id: string | null | undefined) =>
  useQuery({
    queryKey: organizationKeys.tree(id),
    queryFn: () => fetchOrganizationTree({ id: id as string }),
    ...referenceQueryPreset,
    enabled: Boolean(id),
  })

export const useOrganizationNamesByIds = (
  ids: Array<string | null | undefined>,
  options?: {
    enabled?: boolean
    initialNamesById?: Record<string, string>
  },
) => {
  const enabled = options?.enabled ?? true
  const initialNamesById = options?.initialNamesById
  const uniqueIds = useMemo(
    () =>
      [
        ...new Set(ids.filter((id): id is string => typeof id === 'string')),
      ].sort(),
    [ids],
  )

  const knownNamesById = useMemo(() => {
    const next: Record<string, string> = {}
    uniqueIds.forEach((id) => {
      const name = initialNamesById?.[id]
      if (typeof name === 'string' && name.length > 0) {
        next[id] = name
      }
    })
    return next
  }, [initialNamesById, uniqueIds])

  const unknownIds = useMemo(
    () => uniqueIds.filter((id) => !knownNamesById[id]),
    [knownNamesById, uniqueIds],
  )

  const fetchedOrganizations = useQueries({
    queries: unknownIds.map((id) => ({
      ...referenceQueryPreset,
      queryKey: organizationKeys.summary(id),
      queryFn: () => fetchOrganizationSummary({ id }),
      enabled,
    })),
    combine(results) {
      return results.map((result) => result.data)
    },
  })

  return useMemo(() => {
    const merged = { ...knownNamesById }

    unknownIds.forEach((id, index) => {
      const organization = fetchedOrganizations[index]
      if (organization?.name) {
        merged[id] = organization.name
      }
    })

    return merged
  }, [fetchedOrganizations, knownNamesById, unknownIds])
}

export const useQueryOrganization = ({
  id,
}: {
  id: string | null | undefined
}) => useOrganizationTree(id)

const fetchOrganizationMembersByProjectID = async ({
  projectID,
}: {
  projectID: string
}): Promise<OrganizationMember[]> => {
  const { data } = (await client.graphql({
    query: getOrganizationMembersbyProjectIDQuery,
    variables: { projectID },
  })) as GraphQLResult<OrganizationMembersbyProjectIDQuery>

  return (
    data?.organizationMembersbyProjectID?.items
      ?.filter(Boolean)
      .map((item) => memberAdapter(item)) ?? []
  )
}

type OrganizationMembersbyProjectIDQuery = {
  organizationMembersbyProjectID?: {
    items?: OrganizationMemberSchema[] | null
  } | null
}
export const useOrganizationMembersbyProjectID = (
  projectID: string | null | undefined,
) =>
  useQuery({
    queryKey: organizationKeys.membersByProject(projectID),
    queryFn: () =>
      fetchOrganizationMembersByProjectID({ projectID: projectID as string }),
    ...referenceQueryPreset,
    enabled: Boolean(projectID),
    initialData: [] as OrganizationMember[],
  })
